import {useMemo, useRef, useState} from "react";
import {CheckOptionType, FilterSelectorWithTagProps} from "@/pages/datalab/components/FilterSelectorWithTag/type";
import {TagType} from "@/pages/datalab/components/SearchWithTags/type";
import {useMemoizedFn} from "ahooks";
import {getNullTextOfFilter} from "@/pages/datalab/utils/utils";

export const useDefaultFilterSelectorWithTagProps = (props: FilterSelectorWithTagProps) => {
  
  // 受控组件, options, values, onChange必传
  const {options, allOptions, values, maxShowTagCount, maxRemainingCount, onChange, loading} = props;
  
  // 记录用户是否在打开下拉弹窗后, 操作修改过勾选状态 为true, 否则为false;
  const isChangeRef = useRef(false);
  
  // 内置搜索关键字
  const [internalSearchWord, setInternalSearchWord] = useState('');
  
  
  // 搜索状态
  const internalSearchState = !!trimOfSpaces(internalSearchWord);

  // 下拉框显示的选项
  const internalOptions = useMemo(() => {
    if (loading) return [];

    if (!internalSearchState) { // 不是搜索状态下 需要补充 options中不存在的value值
      // 这个逻辑同时兼容 取消勾选后, options中不存在的value值选项会自动消失需求
      const customOptions: CheckOptionType[] = [];
      values?.forEach((value: string) => {
        if (!options.some((opt) => opt?.value === value)) {
          const newValue = getNullTextOfFilter(value);
          customOptions.push({text: newValue, value: newValue, title: newValue});
        }
      });
      return [...customOptions, ...options];
    }
    // 搜索状态下, 优先展示搜索结果
    return options;
  }, [loading, options, values]); // 如果不依赖values, 那么取消勾选后, 自定义选项不会消失
  
  // tags内置显示功能, 兼容父组件传递的tags
  const internalTags = useMemo(() => {
    
    const showTagMaxCount = maxShowTagCount ?? values?.length ?? 0;
    
    const newTags: TagType[] = [];
    values?.slice(0, showTagMaxCount)?.forEach((value) => {
      // newTags.push({value, text: String(value)});
      const opt = options?.find(opt => opt?.value === value) || allOptions?.find(opt => opt?.value === value);
      
      if (opt) {
        newTags.push({value: opt.value, text: opt.text, title: opt.title});
      } else {
        newTags.push({value, text: getNullTextOfFilter(value, true), title: getNullTextOfFilter(value)});
      }
    });
    
    // 除了展示的tag数量的剩余选项数量
    const remainingCount = (values?.length ?? 0) - showTagMaxCount;
    if (newTags.length > 0 && remainingCount > 0) {
      // 如果传递了最大的剩余数据量
      const text = typeof maxRemainingCount === 'number' ? `+${Math.min(remainingCount, maxRemainingCount)}` : `+${remainingCount}`;
      newTags.push({
        value: 'count',
        text,
        disabled: true
      });
    }
    return newTags;
  }, [options, allOptions,values, maxShowTagCount, maxRemainingCount]);
  
  const handleValuesChange = useMemoizedFn(({values, isFull}, context?) => {
    isChangeRef.current = true;
    onChange?.(values, isFull, [], context);

  });
  
  return {
    internalTags, // 内置tags状态, 兼容父组件传递的tags
    internalSearchWord, // 内置 搜索关键字状态, 无父组件参数
    setInternalSearchWord,
    internalSearchState, // 内置 搜索状态, 当父组件传递 searchState时, 采用父组件的searchState
    internalOptions, // 内置 选项数组, 用于兼容内置的搜索逻辑
    handleValuesChange,
    isChangeRef
  };
};

/**
 * 处理字符串中的空格
 * - 如果字符串全部由空格组成，则原样返回
 * - 否则，去除字符串首尾的空格并返回处理后的结果
 * @param {string} str - 输入的字符串
 * @returns {string} 处理后的字符串
 */
export const trimOfSpaces = (str: string): string => {
  // 检查字符串是否全部由空格组成
  if (/^\s*$/.test(str)) {
    return str; // 如果是，原样返回
  }
  return str?.trim(); // 否则，去除首尾空格后返回
};